import { PortalChrome, HealthPill, StatusPill, MOCK, useRole } from "./_shared";
import { Search, Filter, ChevronRight, Plus, Check, AlertTriangle } from "lucide-react";

// Option 1 — "Marketplace" (Shopify Collective + Spocket inspired)
// Left rail: filters. Center: product/relationship grid. Right rail: pinned actions.
// NO TABS. Everything visible at once. Filters drive what shows in the grid.

export default function Option1_Marketplace() {
  const [role, setRole] = useRole();
  const m = MOCK[role];
  const isSeller = role === "seller";

  return (
    <PortalChrome role={role} setRole={setRole} optionLabel="Option 1 · Marketplace" optionNumber="1">
      {/* Sticky page header — matches PortalShell.PageHeader convention */}
      <div className="border-b border-zinc-200 bg-white px-6 lg:px-8 py-5 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dropship</h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            {isSeller ? "Your catalog · mirrored to buyer channels" : "Browse suppliers · one-click add to storefront"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="text-xs font-medium px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50 flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> {isSeller ? "Invite buyer" : "Find supplier"}
          </button>
          <button className="text-xs font-medium px-3 py-1.5 rounded-md bg-zinc-900 text-white flex items-center gap-1.5">
            {isSeller ? "Add product" : "Sync catalog"}
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* LEFT filter rail */}
        <aside className="w-64 flex-shrink-0 border-r border-zinc-200 bg-white p-5 overflow-y-auto">
          <div className="flex items-center gap-2 bg-zinc-50 ring-1 ring-zinc-200 rounded-md px-2.5 py-1.5 mb-5">
            <Search className="w-3.5 h-3.5 text-zinc-400" />
            <input placeholder="Search products…" className="text-xs bg-transparent flex-1 outline-none" />
          </div>

          <FilterGroup label={m.partnersLabel}>
            {m.partners.map(p => (
              <FilterCheckbox key={p.id} label={p.name} count={p.orders30d} />
            ))}
          </FilterGroup>

          <FilterGroup label="Status">
            <FilterCheckbox label="Live" count={isSeller ? m.catalog.reduce((s, x) => s + (x.mirrored || 0), 0) : m.catalog.filter(c => c.inStore).length} checked />
            <FilterCheckbox label="Draft"      count={2} />
            <FilterCheckbox label="MAP flag"   count={1} />
          </FilterGroup>

          <FilterGroup label="Price range">
            <div className="text-[11px] text-zinc-500 space-y-1.5 mt-2">
              <div className="flex items-center gap-2">
                <span>$0</span>
                <div className="flex-1 h-1 bg-zinc-100 rounded-full relative">
                  <div className="absolute inset-y-0 left-[10%] right-[20%] bg-zinc-900 rounded-full"></div>
                </div>
                <span>$100</span>
              </div>
            </div>
          </FilterGroup>

          <FilterGroup label="Category">
            <FilterCheckbox label="Apparel"    count={12} />
            <FilterCheckbox label="Home"       count={8}  />
            <FilterCheckbox label="Food & Bev" count={4}  />
          </FilterGroup>
        </aside>

        {/* CENTER: KPI strip + product grid */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          {/* KPI strip */}
          <div className="grid grid-cols-4 gap-3 mb-6">
            {m.kpis.map(k => (
              <div key={k.label} className="rounded-2xl bg-white border border-zinc-200/80 p-4">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">{k.label}</div>
                <div className={`text-2xl font-semibold tracking-tight mt-1 ${k.accent === "warn" ? "text-amber-600" : "text-zinc-900"}`}>{k.value}</div>
                {k.hint && <div className="text-xs text-zinc-500 mt-1">{k.hint}</div>}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-500">
              {isSeller ? "My catalog" : "Available from suppliers"} · {m.catalog.length}
            </h2>
            <div className="flex items-center gap-1.5 text-xs text-zinc-500">
              <Filter className="w-3 h-3" /> Sort: Most mirrored
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {m.catalog.map(p => (
              <div key={p.sku} className="rounded-2xl bg-white border border-zinc-200/80 p-4 hover:border-zinc-300 hover:shadow-sm transition group">
                <div className="aspect-video rounded-lg bg-gradient-to-br from-zinc-100 to-zinc-50 mb-3 flex items-center justify-center">
                  <span className="text-[10px] text-zinc-400 font-mono">{p.sku}</span>
                </div>
                <div className="text-sm font-medium truncate">{p.name}</div>
                {isSeller ? (
                  <>
                    <div className="grid grid-cols-3 gap-1 mt-2 text-[11px]">
                      <div><div className="text-zinc-400">MSRP</div><div className="font-semibold tabular-nums">${p.msrp}</div></div>
                      <div><div className="text-zinc-400">MAP</div> <div className="font-semibold tabular-nums">${p.map}</div></div>
                      <div><div className="text-zinc-400">WS</div>  <div className="font-semibold tabular-nums">${p.wholesale}</div></div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-zinc-100">
                      <span className="text-[11px] text-zinc-500">Mirrored to <span className="font-semibold text-zinc-900">{p.mirrored} buyers</span></span>
                      <ChevronRight className="w-3.5 h-3.5 text-zinc-300" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{p.supplier}</div>
                    <div className="grid grid-cols-3 gap-1 mt-2 text-[11px]">
                      <div><div className="text-zinc-400">WS</div>    <div className="font-semibold tabular-nums">${p.wholesale}</div></div>
                      <div><div className="text-zinc-400">MSRP</div>  <div className="font-semibold tabular-nums">${p.msrp}</div></div>
                      <div><div className="text-zinc-400">Margin</div><div className="font-semibold tabular-nums text-emerald-700">{Math.round(p.margin * 100)}%</div></div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-zinc-100">
                      {p.inStore
                        ? <span className="text-[11px] font-medium text-emerald-700 flex items-center gap-1"><Check className="w-3 h-3" /> Live in store</span>
                        : <button className="text-[11px] font-medium text-zinc-900 hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add to store</button>}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </main>

        {/* RIGHT rail: pinned actions + relationships */}
        <aside className="w-72 flex-shrink-0 border-l border-zinc-200 bg-white p-5 overflow-y-auto">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Needs attention · 3</div>
          <div className="space-y-2 mb-6">
            <PinnedItem tone="amber" icon={AlertTriangle} title="MAP violation" body="Urban Tots listing at $18 (MAP $19)" />
            <PinnedItem tone="amber" icon={AlertTriangle} title="Order stalled 48h" body="DS-21038 · Urban Tots" />
            <PinnedItem tone="red"   icon={AlertTriangle} title="Sync failed" body="Coastal Mercantile · Shopify" />
          </div>

          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">{m.partnersLabel}</div>
          <div className="space-y-1.5">
            {m.partners.map(p => (
              <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-zinc-50 cursor-pointer">
                <div className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-[10px] font-semibold text-zinc-700">
                  {p.name.split(" ").map(w => w[0]).slice(0, 2).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium truncate">{p.name}</div>
                  <div className="text-[10px] text-zinc-500">${p.gmv30d.toLocaleString()} · {p.orders30d} orders</div>
                </div>
                <HealthPill kind={p.health}>{p.sync === "Real-time" ? "Live" : p.sync}</HealthPill>
              </div>
            ))}
          </div>

          <div className="mt-6 text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Recent orders</div>
          <div className="space-y-2">
            {m.orders.slice(0, 3).map(o => (
              <div key={o.id} className="border border-zinc-200 rounded-lg p-2.5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] text-zinc-500">{o.id}</span>
                  <StatusPill status={o.status} />
                </div>
                <div className="text-xs mt-1 truncate">{o.customer} · ${o.total.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </PortalChrome>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="mb-5">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function FilterCheckbox({ label, count, checked }) {
  return (
    <label className="flex items-center gap-2 py-1 cursor-pointer group">
      <div className={`w-3.5 h-3.5 rounded border ${checked ? "bg-zinc-900 border-zinc-900" : "border-zinc-300 group-hover:border-zinc-400"} flex items-center justify-center`}>
        {checked && <Check className="w-2.5 h-2.5 text-white" />}
      </div>
      <span className="text-xs text-zinc-700 flex-1 truncate">{label}</span>
      {count !== undefined && <span className="text-[10px] text-zinc-400 tabular-nums">{count}</span>}
    </label>
  );
}

function PinnedItem({ tone, icon: Icon, title, body }) {
  const map = {
    amber: "bg-amber-50 border-amber-200",
    red:   "bg-red-50 border-red-200",
  };
  return (
    <div className={`rounded-lg border p-2.5 ${map[tone]}`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-3.5 h-3.5 mt-0.5 ${tone === "amber" ? "text-amber-600" : "text-red-600"}`} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold">{title}</div>
          <div className="text-[10px] text-zinc-600 truncate">{body}</div>
        </div>
      </div>
    </div>
  );
}
